import { useState } from "react";
import { ChevronRight, ShieldCheck, CreditCard, User, Mail, DollarSign, FileText, Check } from "lucide-react";
import { useTranslation, Trans } from 'react-i18next';
import ReCAPTCHA from "react-google-recaptcha";

export function DonationsPlaceToPay() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    currency: "COP",
    amount: "",
    docType: "CC",
    document: "",
    firstName: "",
    lastName: "",
    email: "",
    acceptTerms: false
  });
  const [displayAmount, setDisplayAmount] = useState("");
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);

  const formatAmount = (value: string) => {
    // Elimina cualquier caracter que no sea número
    const rawValue = value.replace(/\D/g, "");
    if (!rawValue) return "";
    // Formatea con separadores de miles
    return new Intl.NumberFormat("es-CO").format(parseInt(rawValue, 10));
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, "");
    setDisplayAmount(formatAmount(raw));
    setFormData((prev) => ({ ...prev, amount: raw }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const val = type === "checkbox" ? (e.target as HTMLInputElement).checked : value;
    setFormData((prev) => ({ ...prev, [name]: val }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!recaptchaToken) {
      alert(t("donations.recaptcha_required", "Por favor completa la verificación de reCAPTCHA para continuar."));
      return;
    }

    // Aquí implementaremos luego la llamada al backend
    console.log("Form data:", { ...formData, recaptchaToken });
  };

  return (
    <div className="relative min-h-[100svh] bg-zinc-950 flex flex-col items-center pt-4 lg:pt-8 pb-20 px-4 sm:px-8 lg:px-12 overflow-hidden">
      {/* Cinematic Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center opacity-20 mix-blend-luminosity bg-fixed"
        style={{ backgroundImage: "url('/assets/hero/ElSeminario.webp')" }}
      ></div>
      <div className="absolute inset-0 bg-gradient-to-b from-zinc-950 via-zinc-950/90 to-zinc-950"></div>
      
      {/* Header Section */}
      <div className="relative z-10 w-full max-w-7xl mx-auto text-center mb-6 lg:mb-10">
        <h1 className="font-neocat text-3xl md:text-5xl lg:text-6xl text-white mb-3 lg:mb-6 tracking-widest uppercase drop-shadow-lg">
          {t("donations.title")}
        </h1>
        <div className="w-16 md:w-24 h-[1px] bg-zinc-500 mx-auto mb-6 lg:mb-8"></div>
        <p className="font-neocat text-base md:text-2xl text-zinc-300 leading-relaxed font-light max-w-3xl mx-auto px-4">
          {t("donations.subtitle")}
        </p>
      </div>

      {/* Main Content Container */}
      <div className="relative z-10 w-full max-w-7xl mx-auto flex flex-col lg:flex-row bg-zinc-900 lg:bg-zinc-900/80 rounded-2xl overflow-hidden border border-zinc-800/50 shadow-2xl">
        
        {/* Left Side: Image (Tonino) */}
        <div className="w-full lg:w-7/12 relative min-h-[350px] lg:min-h-full">
          {/* Contenedor absoluto para garantizar que la imagen cubra siempre el 100% del alto dictado por el formulario */}
          <div className="absolute inset-0 z-0">
            <img 
              src="/images/tonino.jpg" 
              alt="Tonino" 
              className="w-full h-full object-cover object-[20%_center] grayscale-[20%] hover:grayscale-0 transition-all duration-700"
            />
          </div>
          <div className="absolute inset-0 bg-zinc-950/20 z-10"></div>
          <div className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-zinc-900 via-zinc-900/40 lg:via-zinc-900/20 lg:from-transparent to-transparent lg:to-zinc-900/80 z-10"></div>
          
          {/* Quote overlay */}
          <div className="absolute bottom-6 lg:bottom-12 left-6 right-6 lg:left-12 lg:right-12 z-20 text-white border-l-2 border-white/30 pl-4 lg:pl-6">
             <p className="font-sans text-xs md:text-base leading-relaxed text-zinc-200 font-light drop-shadow-md">
              {t("donations.quote_overlay")}
            </p>
          </div>
        </div>

        {/* Right Side: Form - takes the remaining width (w-5/12) */}
        <div className="w-full lg:w-5/12 p-5 sm:p-6 md:p-8 bg-zinc-900 flex flex-col justify-center">
          
          <div className="flex items-center gap-3 mb-5">
            <ShieldCheck className="w-6 h-6 text-zinc-400" />
            <h2 className="font-neocat text-xl font-bold tracking-wider text-white">{t("donations.form_title")}</h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            
            {/* Amount & Currency */}
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-1.5">
                <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-sans">{t("donations.currency_label")}</label>
                <div className="relative">
                  <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <select 
                    name="currency" 
                    value={formData.currency}
                    onChange={handleChange}
                    className="w-full bg-zinc-950/50 border border-zinc-800 text-white rounded-none py-2.5 pl-12 pr-4 focus:outline-none focus:border-zinc-500 transition-colors appearance-none cursor-pointer text-base"
                  >
                    <option value="COP">{t("donations.currency_cop")}</option>
                    <option value="USD">{t("donations.currency_usd")}</option>
                    <option value="EUR">{t("donations.currency_eur")}</option>
                    <option value="MXN">{t("donations.currency_mxn")}</option>
                    <option value="PEN">{t("donations.currency_pen")}</option>
                  </select>
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-sans">{t("donations.amount_label")}</label>
                <div className="relative">
                  <CreditCard className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <input 
                    type="text" 
                    value={displayAmount}
                    onChange={handleAmountChange}
                    required
                    placeholder={t("donations.amount_placeholder")}
                    className="w-full bg-zinc-950/50 border border-zinc-800 text-white rounded-none py-2.5 pl-12 pr-4 focus:outline-none focus:border-zinc-500 transition-colors text-base"
                  />
                </div>
              </div>
            </div>

            {/* Document Type & Number */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-sans">{t("donations.doctype_label")}</label>
                <div className="relative">
                  <FileText className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <select 
                    name="docType"
                    value={formData.docType}
                    onChange={handleChange}
                    className="w-full bg-zinc-950/50 border border-zinc-800 text-white rounded-none py-2.5 pl-12 pr-4 focus:outline-none focus:border-zinc-500 transition-colors appearance-none cursor-pointer text-base"
                  >
                    <option value="CC">{t("donations.doctype_cc")}</option>
                    <option value="CE">{t("donations.doctype_ce")}</option>
                    <option value="NIT">{t("donations.doctype_nit")}</option>
                    <option value="PPN">{t("donations.doctype_ppn")}</option>
                  </select>
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-sans">{t("donations.docnum_label")}</label>
                <input 
                  type="text" 
                  name="document"
                  value={formData.document}
                  onChange={handleChange}
                  required
                  placeholder={t("donations.docnum_placeholder")}
                  className="w-full bg-zinc-950/50 border border-zinc-800 text-white rounded-none py-2.5 px-4 focus:outline-none focus:border-zinc-500 transition-colors text-base"
                />
              </div>
            </div>

            {/* Names */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-sans">{t("donations.names_label")}</label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <input 
                    type="text" 
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    placeholder={t("donations.names_placeholder")}
                    className="w-full bg-zinc-950/50 border border-zinc-800 text-white rounded-none py-2.5 pl-12 pr-4 focus:outline-none focus:border-zinc-500 transition-colors text-base"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-sans">{t("donations.lastnames_label")}</label>
                <input 
                  type="text" 
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  placeholder={t("donations.lastnames_placeholder")}
                  className="w-full bg-zinc-950/50 border border-zinc-800 text-white rounded-none py-2.5 px-4 focus:outline-none focus:border-zinc-500 transition-colors text-base"
                />
              </div>
            </div>

            {/* Email */}
            <div className="space-y-1.5">
              <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-sans">{t("donations.email_label")}</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <input 
                  type="email" 
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder={t("donations.email_placeholder")}
                  className="w-full bg-zinc-950/50 border border-zinc-800 text-white rounded-none py-2.5 pl-12 pr-4 focus:outline-none focus:border-zinc-500 transition-colors text-base"
                />
              </div>
            </div>

            {/* Terms and Conditions */}
            <div className="pt-1 pb-3">
              <label className="flex items-start gap-3 cursor-pointer group">
                <div className="relative flex items-center justify-center mt-0.5">
                  <input 
                    type="checkbox"
                    name="acceptTerms"
                    checked={formData.acceptTerms}
                    onChange={handleChange}
                    required
                    className="appearance-none w-5 h-5 border border-zinc-600 bg-zinc-950/50 checked:bg-zinc-200 checked:border-zinc-200 transition-colors"
                  />
                  {formData.acceptTerms && <Check className="absolute w-3.5 h-3.5 text-zinc-950 stroke-[3]" />}
                </div>
                <span className="text-xs text-zinc-400 font-light leading-relaxed group-hover:text-zinc-300 transition-colors">
                  <Trans i18nKey="donations.terms_accept">
                    Acepto la <a href="/politica-de-privacidad" target="_blank" rel="noopener noreferrer" className="text-white border-b border-white/30 hover:border-white transition-colors">política de tratamiento de la información personal</a>.
                  </Trans>
                </span>
              </label>
            </div>

            {/* reCAPTCHA */}
            <div className="flex justify-center pt-2 pb-3 overflow-hidden rounded-sm">
              <ReCAPTCHA
                sitekey="6LeS-b4tAAAAAKGgVDB_qXadXE2z4AIfT9gZ2Vpd"
                onChange={(token) => setRecaptchaToken(token)}
                theme="dark"
              />
            </div>

            {/* Submit Button */}
            <div className="pt-1">
              <button 
                type="submit"
                className="w-full py-4 bg-white text-zinc-950 font-neocat font-bold tracking-[0.2em] uppercase text-sm flex items-center justify-center gap-3 hover:bg-zinc-200 transition-all duration-300 transform hover:-translate-y-1 shadow-[0_10px_30px_rgba(255,255,255,0.1)]"
              >
                {t("donations.submit_button")}
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
            
            <div className="flex flex-col items-center gap-3 pt-4">
               <div className="text-[10px] tracking-widest text-zinc-500 font-sans uppercase flex items-center gap-2 opacity-50 grayscale">
                  <ShieldCheck className="w-3 h-3" /> {t("donations.secure_payments")}
               </div>
               
               <div className="bg-zinc-950/40 border border-zinc-800/50 rounded-sm p-3 w-full text-center">
                  <p className="text-xs text-zinc-400 font-light leading-relaxed">
                    {t("donations.support_message", "¿Tienes problemas con tu donación? Contáctanos por WhatsApp al")} <br />
                    <a 
                      href="https://wa.me/573217757120" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="inline-block mt-1 text-sm font-semibold text-zinc-300 hover:text-white border-b border-zinc-500/50 hover:border-white transition-all"
                    >
                      +57 321 775 7120
                    </a>
                  </p>
               </div>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
}
